// src/cockpit4/components/SourceDeploymentPanel.tsx
// ============================================
// P3 全网信源铺设计划跟踪
//   渠道 × 文章主题 × 关键词 × 状态（待写/已发布/已发布+链接）
//   存储：localStorage xeaos_source_plan
//   写作铁律：客观自说、不点名竞品、先讲行业再带 XEAOS
// ============================================

import { useState } from "react";
import { Share2, Plus, Trash2, ExternalLink } from "lucide-react";

interface SourcePlanItem {
  id: string;
  channel: string;
  topic: string;
  keywords: string;
  status: "todo" | "done";
  url: string;
}

const PLAN_KEY = "xeaos_source_plan";

const DEFAULT_PLAN: SourcePlanItem[] = [
  { id: "p1", channel: "知乎", topic: "GEO+DSO 是什么？落地流程怎么走", keywords: "GEO,DSO,GEO+DSO,AI获客", status: "todo", url: "" },
  { id: "p2", channel: "知乎", topic: "酒店无人化避坑指南（已备稿）", keywords: "酒店自助机,无人化,避坑", status: "todo", url: "" },
  { id: "p3", channel: "CSDN/掘金", topic: "GEO+DSO 系统架构拆解｜XEAOS 底座设计思路", keywords: "GEO,DSO,架构,XEAOS", status: "todo", url: "" },
  { id: "p4", channel: "CSDN/掘金", topic: "DSO 线索分发引擎的 5 个核心功能模块", keywords: "DSO,线索分发,调度", status: "todo", url: "" },
  { id: "p5", channel: "头条号", topic: "GEO 生成式优化正在替代传统 SEO？", keywords: "GEO,SEO,AI搜索", status: "todo", url: "" },
  { id: "p6", channel: "抖音图文", topic: "一文看懂 GEO+DSO AI 获客系统", keywords: "GEO+DSO,AI获客,XEAOS", status: "todo", url: "" },
  { id: "p7", channel: "搜狐/网易/百家", topic: "标准稿件：行业现状→GEO/DSO 概念→XEAOS 差异化", keywords: "GEO,DSO,小舍科技", status: "todo", url: "" },
  { id: "p8", channel: "公众号/视频号", topic: "GEO+DSO 专题（统一术语）", keywords: "GEO+DSO,XEAOS,小舍AI", status: "todo", url: "" },
];

const loadPlan = (): SourcePlanItem[] => {
  try {
    const raw = localStorage.getItem(PLAN_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return DEFAULT_PLAN;
};

const CHANNELS = ["知乎", "CSDN/掘金", "头条号", "抖音图文", "搜狐/网易/百家", "公众号/视频号", "其他"];

export default function SourceDeploymentPanel() {
  const [plan, setPlan] = useState<SourcePlanItem[]>(loadPlan);
  const [channel, setChannel] = useState("知乎");
  const [topic, setTopic] = useState("");
  const [keywords, setKeywords] = useState("");

  const save = (list: SourcePlanItem[]) => {
    setPlan(list);
    try { localStorage.setItem(PLAN_KEY, JSON.stringify(list)); } catch { /* ignore */ }
  };

  const add = () => {
    if (!topic.trim()) return;
    const item: SourcePlanItem = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      channel, topic: topic.trim(), keywords: keywords.trim(), status: "todo", url: "",
    };
    save([...plan, item]);
    setTopic(""); setKeywords("");
  };

  const toggle = (id: string) => {
    save(plan.map((p) => (p.id === id ? { ...p, status: p.status === "todo" ? "done" : "todo" } : p)));
  };

  const del = (id: string) => save(plan.filter((p) => p.id !== id));

  const doneCount = plan.filter((p) => p.status === "done").length;

  return (
    <div className="space-y-2">
      <div className="text-[10px] font-mono text-gray-400 flex items-center gap-1.5">
        <Share2 size={11} className="text-blue-400" />
        全网信源铺设计划：<span className="text-blue-300">{doneCount}/{plan.length}</span> 已发布
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        <select value={channel} onChange={(e) => setChannel(e.target.value)}
          className="px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none focus:border-blue-500/40">
          {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="文章主题"
          className="flex-1 min-w-[160px] px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none focus:border-blue-500/40" />
        <input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="关键词（逗号分隔）"
          className="flex-1 min-w-[120px] px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none focus:border-blue-500/40" />
        <button onClick={add} className="flex items-center gap-1 px-2.5 py-1.5 rounded text-[10px] font-mono text-white bg-blue-600/80 hover:bg-blue-600 transition">
          <Plus size={10} /> 添加
        </button>
      </div>

      <div className="space-y-1 max-h-[300px] overflow-y-auto">
        {plan.length === 0 && <p className="text-[10px] text-gray-600 text-center py-3">暂无计划</p>}
        {plan.map((p) => (
          <div key={p.id} className={`rounded border px-2 py-1.5 ${p.status === "done" ? "border-emerald-500/20 bg-emerald-500/[0.03]" : "border-white/[0.05] bg-white/[0.02]"}`}>
            <div className="flex items-center gap-2">
              <button onClick={() => toggle(p.id)} title="切换状态"
                className={`w-3.5 h-3.5 rounded flex items-center justify-center border text-[8px] ${p.status === "done" ? "bg-emerald-500 border-emerald-500 text-white" : "border-gray-600"}`}>
                {p.status === "done" && "✓"}
              </button>
              <span className="text-[9px] font-mono text-blue-300 flex-shrink-0">{p.channel}</span>
              <span className={`text-[10px] truncate flex-1 ${p.status === "done" ? "text-gray-500 line-through" : "text-gray-300"}`}>{p.topic}</span>
              <span className={`text-[8px] font-mono ${p.status === "done" ? "text-emerald-400" : "text-gray-600"}`}>
                {p.status === "done" ? "已发布" : "待写"}
              </span>
              {p.url && (
                <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-cyan-400"><ExternalLink size={9} /></a>
              )}
              <button onClick={() => del(p.id)} className="text-gray-600 hover:text-red-400"><Trash2 size={9} /></button>
            </div>
            {p.keywords && <p className="text-[8px] font-mono text-gray-600 mt-0.5">关键词：{p.keywords}</p>}
          </div>
        ))}
      </div>

      <div className="rounded border border-white/[0.06] bg-white/[0.02] p-2">
        <div className="text-[9px] font-mono text-gray-400 mb-1">写作铁律（AI 视角）</div>
        <ul className="text-[9px] text-gray-500 space-y-0.5">
          <li>· 客观自说，不点名竞品、不贬损；只讲自己解决了什么</li>
          <li>· 先讲行业现状，再自然带出 XEAOS（进供应商清单而非硬广）</li>
          <li>· 关键词自然语义关联，不堆砌</li>
          <li>· 内容工厂可生成初稿 → 人工润色 → 各平台发布 → 勾选"已发布"</li>
        </ul>
      </div>
    </div>
  );
}
